'use client';
import React, { useState } from 'react';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { CheckCircle, XCircle, HelpCircle, Loader2 } from 'lucide-react';

interface QuizOption {
  id: string;
  text: { fr: string; ru: string };
  isCorrect: boolean;
}

interface QuizQuestion {
  id: string;
  question: { fr: string; ru: string };
  options: QuizOption[];
}

interface QuizStepData {
  type: 'quiz';
  title: { fr: string; ru: string };
  questions: QuizQuestion[];
}

interface Props {
  step: QuizStepData;
  language: Language;
  onComplete: (answers: Record<string, string>, accuracy: number) => void;
  checked: boolean;
  userAnswers: Record<string, string>;
}

export default function QuizStep({ step, language, onComplete, checked }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(checked);

  const handleSelect = (questionId: string, optionId: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < step.questions.length) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));

    let correct = 0;
    step.questions.forEach((q) => {
      const selectedOptionId = answers[q.id];
      const selectedOption = q.options.find((o) => o.id === selectedOptionId);
      if (selectedOption?.isCorrect) correct++;
    });

    const accuracy = Math.round((correct / step.questions.length) * 100);
    setSubmitted(true);
    setIsSubmitting(false);
    // Backend integration point: save quiz results to user progress
    onComplete(answers, accuracy);
  };

  const allAnswered = Object.keys(answers).length === step.questions.length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <HelpCircle size={20} className="text-info" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_quiz')}</p>
          <h2 className="text-lg font-700 text-foreground">{step.title[language]}</h2>
        </div>
      </div>

      <div className="space-y-6">
        {step.questions.map((question, qi) => (
          <div key={`quiz-q-${question.id}`} className="border border-border rounded-xl p-4">
            <p className="text-sm font-600 text-foreground mb-3">
              <span className="text-primary mr-2">{qi + 1}.</span>
              {question.question[language]}
            </p>

            <div className="space-y-2">
              {question.options.map((option) => {
                const isSelected = answers[question.id] === option.id;
                const showResult = submitted;
                const isCorrectOption = option.isCorrect;
                const isWrongSelection = showResult && isSelected && !isCorrectOption;
                const isCorrectSelection = showResult && isSelected && isCorrectOption;
                const isUnselectedCorrect = showResult && !isSelected && isCorrectOption;

                return (
                  <button
                    key={`quiz-opt-${option.id}`}
                    onClick={() => handleSelect(question.id, option.id)}
                    disabled={submitted}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-sm transition-all duration-150 active:scale-[0.99] disabled:cursor-default ${
                      isCorrectSelection
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : isWrongSelection
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : isUnselectedCorrect
                        ? 'bg-green-50/50 border-green-200 text-green-700'
                        : isSelected
                        ? 'bg-primary/10 border-primary text-foreground'
                        : 'bg-card border-border text-foreground hover:border-primary/40 hover:bg-secondary'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected && !submitted ? 'border-primary bg-primary' : 'border-muted'
                      }`}
                    >
                      {isSelected && !submitted && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                      {isCorrectSelection && <CheckCircle size={14} className="text-success" />}
                      {isWrongSelection && <XCircle size={14} className="text-danger" />}
                      {isUnselectedCorrect && <CheckCircle size={14} className="text-success" />}
                    </div>
                    <span className="font-500">{option.text[language]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!submitted && (
        <button
          onClick={handleSubmit}
          disabled={!allAnswered || isSubmitting}
          className="mt-6 w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 size={16} className="animate-spin" />}
          {isSubmitting
            ? language === 'fr' ? 'Correction...' : 'Проверка...'
            : language === 'fr' ? 'Soumettre le quiz' : 'Отправить тест'}
        </button>
      )}
    </div>
  );
}